import useUserContext from "../../../hooks/useUserContext";
import useAppContext from "../../../hooks/useAppContext";
import useToast from "../../../hooks/useToast";
import React, {useEffect, useMemo, useState} from "react";
import {PresetGroupProps, PresetProps, PresetSubGroupProps} from "../../../types/PresetProps";
import {getTemplatePresets} from "../../../api/getTemplatePresets";
import {deActivateAllTemplates} from "../../../api/deActivateAllTemplates";
import {setDiscountForAllShopProducts} from "../../../api/setDiscountForAllShopProducts";
import {createNotificationFromPresets} from "../../../api/createNotificationFromPresets";
import {getTotalProductsApprovedForDiscount} from "../../../api/getTotalProductsApprovedForDiscount";
import {ToastSkin} from "../../../context/ToastContext";
import Loader from "../../loader/Loader";
import GroupSelectors from "./GroupSelectors";
import PreviewPresets from "./PreviewPresets";
import PresetsExtraActions from "./PresetsExtraActions";
import Step4 from "../../messageCreationFlow/step4/Step4";
import {FlowProps} from "../../../types/FlowProps";
import {MessageTemplateProps} from "../../../types/MessageTemplateProps";

export const DISCOUNT_RATE_FOR_ALL = 3;

interface PresetsModalContentProps {
    isOpen: boolean;
    onClose: (openCoupons?: boolean) => void;
    flowId: number;
    isFlowCreation?: boolean;
}

export interface CouponApprovalStatus {
    total: number | null;
    totalApproved: number | null;
    totalUnApproved: number | null;
}

const PresetsModalContent = ({ isOpen, onClose, flowId, isFlowCreation = false }: PresetsModalContentProps) => {
    const { selectedDomain, user } = useUserContext();
    const { data: { templates, flows }, actions: { loadFlows } } = useAppContext();
    const { showToast } = useToast();
    const [isLoading, setIsLoading] = useState(false);
    const [presets, setPresets] = useState<PresetProps[] | null>(null);
    const [presetGroups, setPresetGroups] = useState<PresetGroupProps[] | null>(null);
    const [presetSubGroups, setPresetSubGroups] = useState<PresetSubGroupProps[] | null>(null);
    const [selectedGroupId, setSelectedGroupId] = useState<number | null>(3);
    const [selectedSubGroupId, setSelectedSubGroupId] = useState<number | null>(null);
    const [isLoadingPresets, setIsLoadingPresets] = useState(false);
    const [productDiscountApprovalStatus, setProductDiscountApprovalStatus] = useState<CouponApprovalStatus>({ total: null, totalApproved: null, totalUnApproved: null });
    const [step, setStep] = useState(1);

    const loadPresets = async () => {
        if(!selectedDomain) return;
        setIsLoadingPresets(true);
        const templatePresetsResponse = await getTemplatePresets(selectedDomain);
        if(templatePresetsResponse?.data) {
            setPresets(templatePresetsResponse.data.presets);
            setPresetGroups(templatePresetsResponse.data.groups);
            setPresetSubGroups(templatePresetsResponse.data.subGroups);
        }
        setIsLoadingPresets(false);
    }

    const createNewTemplatePlan = async (shouldCreateDiscounts: boolean = false, shouldDisableOldTemplates: boolean = false) => {
        if(!selectedDomain) return {
            status: 'failure',
            reason: 'no shop was selected',
        };
        const promises = [];
        if(shouldDisableOldTemplates) {
            promises.push(deActivateAllTemplates(selectedDomain, flowId));
        }
        if(shouldCreateDiscounts) {
            promises.push(setDiscountForAllShopProducts(selectedDomain, DISCOUNT_RATE_FOR_ALL, false));
        }
        const responses = promises?.length > 0 ? await Promise.all(promises) : [];
        let areAllSuccess = true;
        if(responses.length === promises.length) {
            responses.forEach(response => {
                if(areAllSuccess) {
                    areAllSuccess = response.data.status === 'success';
                }
            });
        } else {
            return {
                status: 'failed'
            }
        }

        if(areAllSuccess && selectedDomain) {
            const selectedPresets = presets?.filter(preset => preset.presetGroupId === selectedGroupId && preset.presetSubGroupId === selectedSubGroupId);
            selectedPresets?.length && await createNotificationFromPresets(selectedDomain, flowId, selectedPresets.map(preset => preset.id));
            return {
                status: 'success'
            }
        } else {
            return {
                status: 'failed'
            }
        }
    }

    const onTemplatePlanApprovedBeforeNotes = async () => {
        if(!selectedDomain) return;
        setIsLoading(true);
        const response = await getTotalProductsApprovedForDiscount(selectedDomain);
        setProductDiscountApprovalStatus({ total: response.data.total, totalApproved: response.data.approved, totalUnApproved: response.data.unapproved });
        const activeCurrentTemplates = flows?.data?.find((flow: FlowProps) => flow.id === flowId)?.templates?.find((template: MessageTemplateProps) => template.active);
        if(response.data.total > response.data.approved || activeCurrentTemplates) {
            setStep(3);
        } else {
            const response = await createNewTemplatePlan();
            if(response.status === 'success') {
                setStep(4);
            } else {
                showToast({ displayTime: 12, skin: ToastSkin.error, text: 'The templates creation failed. If error keeps showing up, please contact Support'});
            }
        }
        setIsLoading(false);
    }

    const onTemplatePlanApprovedAfterNotes = async (shouldCreateDiscounts: boolean, shouldDisableOldTemplates: boolean) => {
        if(!selectedDomain) return;
        setIsLoading(true);
        const response = await createNewTemplatePlan(shouldCreateDiscounts, shouldDisableOldTemplates);
        if(response.status === 'success') {
            setStep(4);
        }
        setIsLoading(false);
    }

    const onCloseModal = () => {
        onClose();
        setStep(1);
        setSelectedSubGroupId(null);
        setSelectedGroupId(null);
    }

    const onCloseModalAndReload = () => {
        loadFlows();
        onCloseModal();
    }

    const onSelectGroup = (selectedGroupId: number) => {
        setSelectedGroupId(selectedGroupId);
    }

    const onSelectSubGroup = (selectedSubGroupIdId: number) => () => {
        setIsLoadingPresets(true);
        setSelectedSubGroupId(selectedSubGroupIdId);
        setIsLoadingPresets(false);
    }

    const onSubmitStep1 = () => {
        setStep(2);
    }

    useEffect(() => {
        if(isOpen) {
            setSelectedSubGroupId(null);
            setSelectedGroupId(null);
            loadPresets();
        }
    }, [isOpen]);

    useEffect(() => {
        if(selectedSubGroup) {
            const currentTotalMessages = selectedSubGroup.totalMessages;
            const nextSubGroup = presetSubGroups?.find(subGroup => subGroup.totalMessages === currentTotalMessages && subGroup.groupId === selectedGroupId) || null;
            setSelectedSubGroupId(nextSubGroup?.id || null);
        }
    }, [selectedGroupId]);

    const selectedGroup = useMemo(() => presetGroups ? presetGroups.find(group => group.id === selectedGroupId) : undefined, [presetGroups, selectedGroupId]);
    const selectedSubGroup = useMemo(() => presetSubGroups ? presetSubGroups.find(subGroup => subGroup.id === selectedSubGroupId) : null, [presetSubGroups, selectedSubGroupId]);

    return (
        <div className="presets-modal-container">
            {isLoadingPresets || !presetGroups || !presetSubGroups ?
                <Loader isFullPage={true} label='Creating Templates...'/>
                : <div className="preset-process">
                    {step === 1 && <GroupSelectors presetGroups={presetGroups} presetSubGroups={presetSubGroups} onSelectGroup={onSelectGroup} onSelectSubGroup={onSelectSubGroup} onClose={onCloseModal} selectedSubGroup={selectedSubGroup} selectedGroup={selectedGroup} selectedGroupId={selectedGroupId} selectedSubGroupId={selectedSubGroupId} onSubmit={onSubmitStep1} />}
                    {step === 2 && <PreviewPresets presets={presets!} onTemplatePlanApproved={onTemplatePlanApprovedBeforeNotes} onBackClick={() => setStep(1)} selectedGroup={selectedGroup} selectedSubGroup={selectedSubGroup} isLoading={isLoading} />}
                    {step === 3 && <PresetsExtraActions flowId={flowId} onBack={() => setStep(2)} onSubmit={onTemplatePlanApprovedAfterNotes} couponApprovalStatus={productDiscountApprovalStatus} isLoading={isLoading} />}
                    {step === 4 && <Step4 isPresets={true} onClose={onCloseModalAndReload} formValues={{ title: `${selectedSubGroup?.totalMessages} new ${(selectedSubGroup?.totalMessages || 0) > 1 ? 'templates' : 'template'}` }} isFlowCreation={isFlowCreation} />}
                </div>
            }
        </div>
    )
}

export default PresetsModalContent;